import type {
    IModelInstantiator,
    INSTANTIATOR_INTERFACES,
} from '../../model/interface/instantiator';
import database from '../../private/db';
import type { IDB } from '../../private/db/interface';
import type { IWorkspaceDB } from '../interface/db';
import type { IDBOptions } from '../interface/instantiator';
import type { IWorkspaceTable } from '../interface/table';
import type { DataMap, IDBVersionMigrator } from '../private/migration/interface';
import tableConstructor from './table';

/* Api data from serve response for type of Interface */
type ApiData<Interface extends keyof INSTANTIATOR_INTERFACES> = Parameters<
    INSTANTIATOR_INTERFACES[Interface]
>[0];

class ContainerDB implements IWorkspaceDB {
    name = '';

    private _db: IDB;

    private _openCount = 0;

    private _modelInstantiator: IModelInstantiator;

    private _migrators: IDBVersionMigrator[];

    constructor(
        modelInstantiator: IModelInstantiator,
        migrators: IDBVersionMigrator[],
        database_: IDB,
    ) {
        this._db = database_;

        for (const migrator of migrators.sort((itemA, itemB) => itemA.version - itemB.version)) {
            const storesTask = migrator.mappingsChanges ?? {};
            const upgradeTask = migrator.upgrade ?? (() => Promise.resolve());

            this._db.version(migrator.version).stores(storesTask).upgrade(upgradeTask);
        }

        this._modelInstantiator = modelInstantiator;
        this._migrators = migrators;
        this.name = this._db.name;
    }

    async open(): Promise<ContainerDB> {
        this._openCount += 1;
        if (this._openCount <= 1) {
            await this._db.open();
        }

        return this;
    }

    close(): void {
        if (this._db && this.isOpen() && this._openCount <= 1) {
            this._db.close();
        }

        this._openCount = Math.max(0, this._openCount - 1);
    }

    isOpen(): boolean {
        return this._db.isOpen() ?? false;
    }

    preInsertCleanup(dataIn: DataMap): DataMap {
        let dataOut = dataIn;

        for (const migrator of this._migrators) {
            dataOut = migrator.preInsertCleanup?.(dataOut) ?? dataOut;
        }

        return dataOut;
    }

    table<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Constructor extends INSTANTIATOR_INTERFACES[Interface],
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(
        tableName: Interface,
    ): IWorkspaceTable<Interface, INSTANTIATOR_INTERFACES[Interface], Entity> {
        const table = this._db.table<ApiData<Interface>, keyof ApiData<Interface>>(tableName);
        return tableConstructor<Interface, Constructor, Entity>(tableName, table);
    }

    first<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(tableName: Interface): Promise<Entity> {
        if (!tableName) {
            return Promise.reject(new Error('No Table to search with'));
        }

        const database_ = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        return new Promise<Entity>((resolve, reject) => {
            database_
                .table(tableName)
                .toArray()
                .then(items => {
                    const [item] = items;
                    if (item) {
                        const object = instantiator(tableName, item);
                        resolve(object as Entity);
                    } else {
                        reject(new Error(`No items found in table: ${tableName}`));
                    }
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    }

    async get<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
        PrimaryKey extends ReturnType<Entity['primaryKey']>,
    >(tableName: Interface, pk: PrimaryKey): Promise<Entity> {
        if (!pk) {
            return Promise.reject(new Error(`No PK to search with, table:${tableName}`));
        }

        const database_ = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        const data = (await database_.table(tableName).get(pk)) as Entity | undefined;

        if (data && typeof data === 'object') {
            const item = instantiator(tableName, data);
            return item as Entity;
        } else {
            throw new Error(`No data found in ${tableName} matching pk:${pk.toString()}`);
        }
    }

    async all<
        Interface extends keyof INSTANTIATOR_INTERFACES,
        Entity extends ReturnType<INSTANTIATOR_INTERFACES[Interface]>,
    >(tableName: Interface): Promise<Entity[]> {
        if (!tableName) {
            return Promise.reject(new Error('No Table to search with'));
        }

        const database_ = this._db;
        const instantiator = this._modelInstantiator.instantiate;

        const itemsData = await database_.table(tableName).toArray();

        const items: Entity[] = itemsData.map(itemData => instantiator(tableName, itemData));

        return items;
    }

    flatten(): Promise<string> {
        return this._db.flatten();
    }

    apply(data: string): Promise<void> {
        return this._db.apply(data);
    }
}

async function create(
    modelInstantiator: IModelInstantiator,
    migrators: IDBVersionMigrator[],
    options: IDBOptions = {},
): Promise<ContainerDB> {
    const databaseMigrator = migrators[migrators.length - 1];

    const database_ = await database().open(options.name ?? 'Workspace', {
        version: databaseMigrator?.version ?? 1,
        mappings: databaseMigrator?.mappingsChanges ?? {},
    });

    const controller = new ContainerDB(modelInstantiator, migrators, database_);

    return controller;
}

export default create;
